/**
 * Created by robert.lavoie on 10/23/17.
 */

import { Component, Input, Output, EventEmitter, OnInit, Inject, PLATFORM_ID, ViewChild, ElementRef, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Subscription } from 'rxjs';


// Services
import { ReportingService, GeoLocationService, SiteService, BrowserService } from '../../services';


// Models
import { LatLng } from '../../models';

interface Window {
  google: any;
}

declare var window: Window;

/**
 *
 * @class
 * JobsSearchResultsMapViewComponent
 *
 * @description
 * This component displays a list of jobs based on your search
 * There are two view types map and list
 *
 */
@Component({
  templateUrl: './jobs-search-results-map-view.component.html',
  selector: 'jobs-search-results-map-view',
  styleUrls: ['./jobs-search-results-map-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOut', [
      state('in', style({
        transform: 'translate3d(0, 0, 0)',
        opacity: 1
      })),
      transition('void => *', [
        style({
          transform: 'translate3d(-50%, 0, 0)',
          opacity: 0
        }),
        animate('200ms ease-in-out')
      ]),
      transition('* => void', [
        animate('200ms ease-in-out', style({
          opacity: 0
        }))
      ])
    ])
  ]
})
export class JobsSearchResultsMapViewComponent implements OnInit, OnChanges {

  @ViewChild('googleMap') googleMap: ElementRef;
  @ViewChild('resultList') resultList: ElementRef;

  // Array array of job results from your search
  @Input() jobResults: object[];

  // The sites theme color
  @Input() themeColor: string;

  // Is set to true after a map search has completed
  @Input() hasSearched: boolean;

  // Emits and event to the host searches this area
  @Output() newMapCoords: EventEmitter<any> = new EventEmitter();

  // Emits and event to the host to run the geo locate function
  @Output() getLatLngFromGeoLocation: EventEmitter<any> = new EventEmitter();

  private defaultLatLng: LatLng  = { lat: 40.111689, lng: -97.382812 };

  // An instance of google maps
  private map;

  // An instance of google maps infoWindow object
  private infoWindow;

  // An google maps marker object
  private markerImage: object;

  // Dynamic content on the for the location markers
  private infoWindowContent: string;

  // Markers is an array of google maps markers
  private markers: any[] = [];

  // This is set to true after a user moves the map which allows them to research that area
  public canSearch: boolean = false;

  // This is set to arrays of jobs grouped by lat long and ordered by distance
  private groupedJobsArr = [];

  // Flattened version of groupedJobsArr
  public groupedJobsMerged = [];

  // This is set to jobs for a location after the marker is clicked
  public jobsForSelectedLocation = [];

  // zoom number to start showing the please zoom in message below
  private pleaseZoomLevel: number = 9;

  // This is used for determining device mode
  browserServiceSubscription$: Subscription;
  public mode: string;

  // determines whether or not the map is dragable or zoomable
  public mapDisabled: boolean;

  // toggles to map message overlay
  public showOverlay: boolean;

  // This is the text for the map overlay.
  public overlayText: string;

  // different messages for the overlay
  public textMessages: object = {
    noLocation: 'Enter your location in the search box to see open jobs in your area.',
    noZoom: 'Please zoom in to search for jobs.',
    noJobs: 'No open jobs in this area. Try resetting your filters, search a new location or check back later for new jobs.'
  };

  // toggles overlay close button
  public overlayHasClose: boolean;

  constructor(
    private browserService: BrowserService,
    private ref: ChangeDetectorRef,
    private reportingService: ReportingService,
    private router: Router,
    private siteService: SiteService,
    public geoLocationService: GeoLocationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // bind to browser service
      this.browserServiceSubscription$ = this.browserService.mode$.subscribe((value) => {
        this.mode = value;
        if (this.map)
          this.map.setOptions({ draggable: !this.mapDisabled, zoomControl: (this.mode === 'desktop' && !this.mapDisabled), scrollwheel: true });

        this.ref.detectChanges();
      });

      this.initMap();
    }

  }


  /**
   * Gets called anytime the input properties get changed
   *
   * @param { SimpleChanges }
   */
  ngOnChanges(changes: SimpleChanges) {
    // Render the map based on the current jobs
    if (changes.jobResults && changes.jobResults.firstChange === false && changes.jobResults.currentValue.length > 0 && isPlatformBrowser(this.platformId)){
      this.canSearch = false;
      this.renderMap(changes.jobResults.currentValue);
    } else if (changes.jobResults.currentValue.length < 1 && this.hasSearched) {
      this.jobsForSelectedLocation = [];
      this.clearMarkers();
      this.groupedJobsMerged = [];
      this.overlayText = this.textMessages['noJobs'];
      this.overlayHasClose = true;
      this.showOverlay = true;
      this.ref.detectChanges();
    }
  }

  ngOnDestroy() {
    this.browserServiceSubscription$.unsubscribe();
  }

  /**
   * Initializes google maps with some base config options
   * We always start centered on the US if there is no location
   */
  initMap() {

    this.markerImage = {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: this.themeColor,
      fillOpacity: 1,
      scale: 8,
      strokeColor: 'white',
      strokeWeight: 2
    };

    this.map = new window.google.maps.Map(this.googleMap.nativeElement, {
      mapTypeId: 'roadmap',
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      zoomControl: (this.mode === 'desktop'),
      scaleControl: (this.mode !== 'desktop'),
      styles: [
        {
          featureType: 'poi.business',
          stylers: [
            {
              visibility: 'off'
            }
          ]
        }
      ],
      center: this.defaultLatLng,
      zoom: 4
    });


    this.infoWindow = new window.google.maps.InfoWindow({
      content: this.infoWindowContent,
      maxWidth: 200
    });

    this.initMapEvents();
    if (this.jobResults.length > 0){
      this.renderMap(this.jobResults);
    } else if (!this.hasSearched) {
      this.mapDisabled = true;
      this.map.setOptions({ draggable: false, zoomControl: false, scrollwheel: false });
      this.overlayText = this.textMessages['noLocation'];
      this.overlayHasClose = false;
      this.showOverlay = true;
      this.ref.detectChanges();
    } else if (this.hasSearched) {
      this.clearMarkers();
      this.overlayText = this.textMessages['noJobs'];
      this.overlayHasClose = true;
      this.showOverlay = true;
      this.ref.detectChanges();
    }
  }

  /**
   * Initializes all map events
   */
  initMapEvents(){

    this.map.addListener('dragstart', ()  => {
      this.canSearch = false;
      this.ref.detectChanges();
    });

    this.map.addListener('dragend', ()  => {
      setTimeout(
        () => {
          this.canSearch = true;
          this.ref.detectChanges();
        },
        200);
    });


    this.map.addListener('zoom_changed', () => {
      if (this.map.zoom < this.pleaseZoomLevel) {
        this.overlayText = this.textMessages['noZoom'];
        this.overlayHasClose = false;
        this.showOverlay = true;
        this.ref.detectChanges();
      } else {
        this.showOverlay = false;
        this.ref.detectChanges();
      }
    });

  }

  /**
   * Groups all jobs by their lat long
   *
   * @param { Array } - An array of jobs
   * @return { Array } - An array of arrays by location
   */
  groupJobsByLocation(jobs: object[]): object[][] {
    const jobsGrouped = [];
    let previousLatLong = [];

    // sort array by lat then long
    const sortedArray:  object[] = jobs.sort((a: object, b: object): any => {
      if (a['latitude'] === b['latitude']){
        return (b['longitude'] - b['longitude']);
      } else {
        return (a['latitude'] - b['latitude']);
      }
    });

    for (let i: number = 0 ; i < sortedArray.length; i += 1){
      if (!previousLatLong[1] || sortedArray[i]['latitude'] !== previousLatLong[0] && sortedArray[i]['longitude'] !== previousLatLong[1]){
        jobsGrouped.push([sortedArray[i]]);
        previousLatLong = [sortedArray[i]['latitude'], sortedArray[i]['longitude']];
      } else {
        jobsGrouped[jobsGrouped.length - 1 ].push(sortedArray[i]);
      }
    }
    return jobsGrouped.sort((a, b) => a[0]['distance'] - b[0]['distance']);
  }

  /**
   * plots the markers and zooms map to the marker bounds
   *
   * @param { Array } - An array of jobs
   */

  renderMap(jobs: object[]) {
    this.mapDisabled = false;
    window.google.maps.event.trigger(this.map, 'resize');
    this.map.setOptions({ draggable: true, zoomControl: (this.mode === 'desktop'), scrollwheel: true });
    this.clearMarkers();
    // group jobs
    this.groupedJobsArr = this.groupJobsByLocation(jobs);
    this.groupedJobsMerged = [].concat(...this.groupedJobsArr);
    this.jobsForSelectedLocation = [];

    this.ref.detectChanges();

    const bounds = new window.google.maps.LatLngBounds();

    this.markers = this.groupedJobsArr.map((jobArr, index) => {
      const marker = new window.google.maps.Marker({
        position: { lat: +jobArr[0][ 'latitude' ], lng: +jobArr[0][ 'longitude' ] },
        animation: window.google.maps.Animation.DROP,
        title: `${jobArr[0]['addressLine1']} ${jobArr[0]['city']}, ${jobArr[0]['stateProvCode']}`,
        jobArrIndex: index,
        allJobs: jobArr,
        icon: this.markerImage
      });

      // this is where the styling for the pop-up infoWindow card lives
      window.google.maps.event.addListener(marker, 'click', (e) => {
        e.stop();
        const postingstext = (marker.allJobs.length > 1) ? 'postings' : 'posting';

        this.infoWindow.setContent(`<h6>${marker.title}</h6>
        <div style='color: #ffffff; padding: 6px 8px; font-size:12px; display:inline-block; text-align:center; border-radius: 10px; background-color:${this.themeColor}'>
        ${marker.allJobs.length} job ${postingstext}</div>`);

        this.setJobsForSelectedLocation(marker.allJobs);

        if (this.mode === 'desktop') {
          this.infoWindow.open(this.map, marker);
          this.setJobScrollPosition();
        }

        this.reportingService.trackCustomEvent('cp_map_pin');

      });

      bounds.extend(marker.getPosition());

      return marker;
    });

    // set map to close on click
    window.google.maps.event.addListener(this.map, 'click', () => this.deselectInfoWindow());

    // Bind close events for the infoWindow
    window.google.maps.event.addListener(this.infoWindow, 'closeclick', () => {
      this.deselectInfoWindow();
      this.reportingService.trackCustomEvent('cp_map_pinbox_close');
    });

    this.map.fitBounds(bounds, 0);
    this.map.panToBounds(bounds);

    setTimeout(
    () => {
      this.setMapOnAll(this.map);
    },
    500);
  }

  /**
   * deselecting infoWindow and resetting selected jobs
   */
  deselectInfoWindow() {
    this.jobsForSelectedLocation = [];
    this.ref.detectChanges();
    this.infoWindow.close();
  }

  /**
   * Plots the markers on a map
   *
   * @param { any } - An instance of google maps or null to clear
   */
  setMapOnAll(map: any) {
    // Sets the map on all markers in the array.
    this.markers.map((marker) => {
      marker.setMap(map);
    });
  }


  /**
   * Clears all markers within a map
   */
  clearMarkers(){
    this.setMapOnAll(null);
    this.markers = [];
  }


  /**
   * Emits a NE and SW coords for a new map search
   */
  emitMapCoords() {

    const NE  = this.map.getBounds().getNorthEast();
    const SW  = this.map.getBounds().getSouthWest();

    const coords:  { ne: LatLng; sw: LatLng; } = {
      ne: {
        lat: NE.lat(),
        lng : NE.lng()
      },
      sw: {
        lat : SW.lat(),
        lng : SW.lng()
      }
    };

    this.newMapCoords.emit(coords);
    this.reportingService.trackCustomEvent('cp_map_redosearch');

  }

  /**
   * sets the scroll position of the job list to the first selected job
   */
  setJobScrollPosition(){
    const resultListNative = this.resultList.nativeElement;
    resultListNative.scroll({ top: resultListNative.querySelector('.active').offsetTop, left: 0, behavior: 'smooth' });
  }

  /**
   * Sets the array of jobs for the selected location
   *
   * @param { array } - an array of job objects
   */
  setJobsForSelectedLocation(jobs: object[]) {
    this.jobsForSelectedLocation = jobs;
    this.ref.detectChanges();
  }

  /**
  * Checks to see if the current job is part of the current location
  *
  * @param { object } - job
  * @param { boolean }
  */
  public isCurrentLocation(job: object): boolean {
    return(this.jobsForSelectedLocation.includes(job));
  }

}